// §4.1's MRP half of the workbench (P3-H-2, D-388 ruling 8, D-387 ruling 5):
// the MRP tile with Run MRP and Run group-wide, the planned-order and
// action-message tiles, the failure count, the last group pass's fixpoint, the
// nightly skip, the weeks over the approved MPS and the run history with its
// drill-in. The workbench mounts it only for a holder of
// `planning.planned_order.read`, so a sales reader sees none of it.

import { useEffect, useState } from "react";
import { Link, useNavigate } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { Button } from "../../../../components/button";
import { Card } from "../../../../components/card";
import { DataTable, type DataTableColumn } from "../../../../components/data-table";
import { StatStrip, StatTile } from "../../../../components/stat-strip";
import { EmptyState, ErrorState } from "../../../../components/states";
import { Banner } from "../../../../components/toast";
import { formatTimestamp } from "../../../../lib/datetime";
import { UnauthorizedError } from "../../../../lib/problem";
import { requestIdOf } from "../../../../lib/request";
import {
  ACTION_MESSAGE_KINDS,
  PLANNED_ORDER_KINDS,
  useMrpRuns,
  usePlannedOrderSummary,
  type MrpRun,
  type MrpRunList,
  type PlannedOrderSummary,
} from "../hooks/mrp-queries";
import { usePlanningPermissions } from "../hooks/permissions";
import {
  formatRunDuration,
  graphExcluded,
  lastEndedRun,
  lastOrchestratedRun,
  mrpRunDuration,
  nightlySkipped,
  queuedRun,
} from "../lib/mrp-lib";
import { formatDate, useOffline } from "../lib/planning-lib";
import { usePlanningFacility } from "../planning-layout";
import { MrpErrorLine, MrpRunStatusChip, MrpTriggerChip, useMrpRunControl } from "./mrp-bits";
import { shortId } from "../../../../lib/short-id";

export function MrpWorkbenchSection() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { facility, facilities } = usePlanningFacility();
  const perms = usePlanningPermissions();
  const canRun = perms.has("planning.mrp.run");
  const zone = facility.timezone;
  const offline = useOffline();

  const [cursors, setCursors] = useState<(string | undefined)[]>([undefined]);
  const page = cursors.length - 1;
  const head = useMrpRuns(facility.id, undefined);
  const history = useMrpRuns(facility.id, cursors[page]);
  const summary = usePlannedOrderSummary(facility.id);
  const nameOf = (id: string) => facilities.find((f) => f.id === id)?.name ?? shortId(id);
  const control = useMrpRunControl(facility.id, nameOf, zone, {
    canRun,
    canGroup: perms.isGroupAdmin,
  });

  useEffect(() => {
    setCursors([undefined]);
  }, [facility.id]);

  const authError = [head.error, history.error, summary.error].find(
    (e) => e instanceof UnauthorizedError,
  );
  useEffect(() => {
    if (authError !== undefined) void navigate({ to: "/login" });
  }, [authError, navigate]);

  const time = (iso: string | null) => formatTimestamp(iso, i18n.language, zone);
  const sectionHead = (
    <div className="planning-mrp-head">
      <h2 id="planning-mrp-h">{t("planning.mrp.title")}</h2>
      <div className="planning-actions">
        {control.groupButton}
        {control.button}
      </div>
    </div>
  );

  if (head.error !== null && authError === undefined) {
    return (
      <section
        className="planning-mrp"
        aria-labelledby="planning-mrp-h"
        data-testid="planning-mrp-section"
      >
        {sectionHead}
        <ErrorState
          title={t("planning.mrp.loadError")}
          retryLabel={t("planning.common.retry")}
          onRetry={() => void head.refetch()}
          requestId={requestIdOf(head.error)}
          requestIdLabel={t("planning.common.requestId")}
        />
      </section>
    );
  }

  const list = head.data;
  const failures = list?.consecutive_failures ?? 0;
  const latestSucceeded = list?.items.find((r) => r.status === "succeeded");
  const exceeded = latestSucceeded?.stats.mps_exceeded_count ?? 0;
  const orchestrated = lastOrchestratedRun(list);
  const fixpoint = orchestrated?.stats.orchestration?.fixpoint ?? null;
  const skipped = nightlySkipped(list, zone);

  const rerun = canRun ? (
    <Button
      size="sm"
      data-testid="planning-mrp-rerun"
      disabled={control.running || offline}
      onClick={(e) => {
        e.stopPropagation();
        control.start();
      }}
    >
      {t("planning.mrp.rerun")}
    </Button>
  ) : null;

  const columns: DataTableColumn<MrpRun>[] = [
    {
      key: "run",
      header: t("planning.mrp.columns.run"),
      id: true,
      render: (row) => (
        <Link
          to="/planning/mrp/runs/$runId"
          params={{ runId: row.id }}
          data-testid="planning-mrp-run-link"
          onClick={(e) => {
            e.stopPropagation();
          }}
        >
          {row.run_no}
        </Link>
      ),
    },
    {
      key: "created",
      header: t("planning.mrp.columns.created"),
      age: true,
      render: (row) => time(row.started_at ?? row.created_at),
    },
    {
      key: "trigger",
      header: t("planning.mrp.columns.trigger"),
      render: (row) => <MrpTriggerChip trigger={row.trigger} />,
    },
    {
      key: "status",
      header: t("planning.mrp.columns.status"),
      render: (row) => <MrpRunStatusChip status={row.status} amber={graphExcluded(row.stats)} />,
    },
    {
      key: "duration",
      header: t("planning.mrp.columns.duration"),
      numeric: true,
      render: (row) => {
        const d = mrpRunDuration(row);
        return d === undefined ? "" : formatRunDuration(t, d);
      },
    },
    {
      key: "createdOrders",
      header: t("planning.mrp.columns.createdOrders"),
      numeric: true,
      render: (row) => row.stats.planned_orders_created ?? "",
    },
    {
      key: "updatedOrders",
      header: t("planning.mrp.columns.updatedOrders"),
      numeric: true,
      render: (row) => row.stats.planned_orders_updated ?? "",
    },
    {
      key: "cancelledOrders",
      header: t("planning.mrp.columns.cancelledOrders"),
      numeric: true,
      render: (row) => row.stats.planned_orders_cancelled ?? "",
    },
    {
      key: "messages",
      header: t("planning.mrp.columns.messages"),
      numeric: true,
      render: (row) => row.stats.action_messages ?? "",
    },
    {
      key: "error",
      header: t("planning.mrp.columns.error"),
      render: (row) =>
        row.status === "failed" ? (
          <span className="planning-refresh-failed">
            <MrpErrorLine run={row} />
            {rerun}
          </span>
        ) : graphExcluded(row.stats) ? (
          <span className="planning-warn" data-testid="planning-mrp-graph-excluded">
            {t("planning.mrp.graphExcluded")}
          </span>
        ) : null,
    },
  ];

  return (
    <section
      className="planning-mrp"
      aria-labelledby="planning-mrp-h"
      data-testid="planning-mrp-section"
    >
      {sectionHead}
      {control.banner}
      {failures > 0 ? (
        <Banner kind="bad" data-testid="planning-mrp-failures">
          {t("planning.mrp.failures", { count: failures })}
        </Banner>
      ) : null}
      {skipped && list?.nightly_mrp_on ? (
        <p className="planning-note" data-testid="planning-mrp-nightly-skipped">
          {t("planning.mrp.nightlySkipped", {
            date: formatDate(list.nightly_mrp_on, i18n.language),
          })}
        </p>
      ) : null}
      {orchestrated !== undefined && fixpoint !== null ? (
        <p
          className={fixpoint === "converged" ? "planning-note" : "planning-note planning-bad"}
          data-testid="planning-mrp-fixpoint"
          data-fixpoint={fixpoint}
        >
          {t(`planning.mrp.fixpoint.${fixpoint}`, {
            pass: orchestrated.stats.orchestration?.pass_no ?? orchestrated.pass_no ?? 1,
          })}
        </p>
      ) : null}
      {exceeded > 0 ? (
        <Banner kind="warn" data-testid="planning-mrp-mps-exceeded">
          {t("planning.mrp.mpsExceeded", { count: exceeded })}{" "}
          <Link to="/planning/mps" data-testid="planning-mrp-propose-link">
            {t("planning.mrp.proposeNow")}
          </Link>
        </Banner>
      ) : null}
      <MrpTiles
        list={list}
        loading={head.isPending}
        summary={summary.data}
        summaryLoading={summary.isPending}
        summaryFailed={summary.error !== null}
        format={time}
      />
      <Card title={t("planning.mrp.historyTitle")}>
        <DataTable
          columns={columns}
          rows={history.data?.items ?? []}
          rowKey={(r) => r.id}
          rowTestId="planning-mrp-run-row"
          rowClassName={(r) =>
            r.status === "failed"
              ? "planning-row-failed"
              : r.status === "succeeded" && graphExcluded(r.stats)
                ? "planning-row-amber"
                : undefined
          }
          rowProps={(r) => ({ "data-run-id": r.id, "data-status": r.status })}
          onRowClick={(r) => {
            void navigate({ to: "/planning/mrp/runs/$runId", params: { runId: r.id } });
          }}
          loading={history.isPending}
          loadingLabel={t("planning.mrp.loading")}
          caption={t("planning.mrp.historyTitle")}
          empty={
            <EmptyState title={t("planning.mrp.emptyTitle")} body={t("planning.mrp.emptyBody")} />
          }
          error={
            history.error !== null ? (
              <ErrorState
                title={t("planning.mrp.loadError")}
                retryLabel={t("planning.common.retry")}
                onRetry={() => void history.refetch()}
                requestId={requestIdOf(history.error)}
                requestIdLabel={t("planning.common.requestId")}
              />
            ) : undefined
          }
          pager={{
            range: t("planning.common.page", { page: page + 1 }),
            page: page + 1,
            onPage: (n) => {
              if (n > page + 1 && history.data?.next_cursor)
                setCursors([...cursors, history.data.next_cursor]);
              else if (n < page + 1) setCursors(cursors.slice(0, -1));
            },
            prevLabel: t("planning.common.previous"),
            nextLabel: t("planning.common.next"),
            hasPrev: page > 0,
            hasNext: (history.data?.next_cursor ?? null) !== null,
          }}
        />
      </Card>
    </section>
  );
}

function MrpTiles({
  list,
  loading,
  summary,
  summaryLoading,
  summaryFailed,
  format,
}: {
  list: MrpRunList | undefined;
  loading: boolean;
  summary: PlannedOrderSummary | undefined;
  summaryLoading: boolean;
  summaryFailed: boolean;
  format: (iso: string | null) => string;
}) {
  const { t } = useTranslation();
  const last = lastEndedRun(list);
  const queued = queuedRun(list);
  const open =
    summary === undefined
      ? 0
      : PLANNED_ORDER_KINDS.reduce(
          (n, k) => n + summary.by_status.proposed[k] + summary.by_status.firmed[k],
          0,
        );
  const messages =
    summary === undefined
      ? 0
      : ACTION_MESSAGE_KINDS.reduce((n, k) => n + summary.action_messages[k], 0);
  return (
    <StatStrip data-testid="planning-mrp-tiles">
      <StatTile
        label={t("planning.mrp.tiles.mrp")}
        loading={loading}
        data-testid="planning-tile-mrp"
        value={
          last === undefined ? (
            "–"
          ) : (
            <Link
              to="/planning/mrp/runs/$runId"
              params={{ runId: last.id }}
              className="planning-mps-tile-link"
              data-run-id={last.id}
            >
              {last.run_no}
            </Link>
          )
        }
        sub={
          last === undefined && queued === undefined ? (
            t("planning.mrp.tiles.noRun")
          ) : (
            <span className="planning-mps-tile-sub">
              {last !== undefined ? (
                <>
                  <MrpTriggerChip trigger={last.trigger} />
                  <MrpRunStatusChip
                    status={last.status}
                    amber={graphExcluded(last.stats)}
                    testId="planning-tile-mrp-status"
                  />
                  <span>{format(last.finished_at ?? last.created_at)}</span>
                </>
              ) : null}
              {queued !== undefined ? (
                <span data-testid="planning-tile-mrp-queued" data-due-at={queued.due_at}>
                  {t("planning.mrp.tiles.queued", { time: format(queued.due_at) })}
                </span>
              ) : null}
            </span>
          )
        }
      />
      <StatTile
        label={t("planning.mrp.tiles.orders")}
        loading={summaryLoading}
        data-testid="planning-tile-planned-orders"
        value={
          summary === undefined ? (
            "–"
          ) : (
            <Link to="/planning/orders" className="planning-mps-tile-link">
              {open}
            </Link>
          )
        }
        sub={
          summaryFailed ? (
            t("planning.mrp.tiles.summaryError")
          ) : summary === undefined ? undefined : (
            <span className="planning-tile-counts" data-testid="planning-tile-orders-counts">
              {(["proposed", "firmed"] as const).map((status) => (
                <span key={status} data-status={status}>
                  {t(`planning.mrp.tiles.byStatus.${status}`, {
                    count: PLANNED_ORDER_KINDS.reduce(
                      (n, k) => n + summary.by_status[status][k],
                      0,
                    ),
                  })}
                </span>
              ))}
              {PLANNED_ORDER_KINDS.filter(
                (k) => summary.by_status.proposed[k] + summary.by_status.firmed[k] > 0,
              ).map((k) => (
                <span key={k} data-kind={k}>
                  {t(`planning.mrp.tiles.byKind.${k}`, {
                    count: summary.by_status.proposed[k] + summary.by_status.firmed[k],
                  })}
                </span>
              ))}
              {summary.purchase_only ? <span>{t("planning.orders.purchaseOnlyShort")}</span> : null}
            </span>
          )
        }
      />
      <StatTile
        label={t("planning.mrp.tiles.messages")}
        loading={summaryLoading}
        data-testid="planning-tile-action-messages"
        value={
          summary === undefined ? (
            "–"
          ) : (
            <Link
              to="/planning/orders"
              search={{ view: "messages" }}
              className="planning-mps-tile-link"
              data-testid="planning-tile-messages-link"
            >
              {messages}
            </Link>
          )
        }
        sub={
          summary === undefined ? undefined : messages === 0 ? (
            t("planning.mrp.tiles.noMessages")
          ) : (
            <span className="planning-tile-counts" data-testid="planning-tile-messages-counts">
              {ACTION_MESSAGE_KINDS.filter((k) => summary.action_messages[k] > 0).map((k) => (
                <span key={k} data-kind={k}>
                  {t(`planning.orders.message.${k}`)} {summary.action_messages[k]}
                </span>
              ))}
            </span>
          )
        }
      />
    </StatStrip>
  );
}
